import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FiShoppingCart, FiPlus, FiImage, FiXCircle } from 'react-icons/fi';
import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import { backendUrl } from '../../../../utilities/axios';
import { getMyShopItems, cancelShopItem } from '../../../../store/shopItems/shopItemsSlice';
import { NoData } from '../../../shared/WebsiteFeedback';

const Page = styled.div`
  padding: 22px 24px 40px;
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 22px;
  flex-wrap: wrap;
  .title { font-size: 20px; font-weight: 800; color: #f1f5f9; display: flex; align-items: center; gap: 10px; }
  .sub { font-size: 12.5px; color: #6b7280; margin-top: 4px; }
`;

const AddLink = styled(Link)`
  display: flex; align-items: center; gap: 6px;
  padding: 9px 18px; background: #9333EA; border-radius: 8px;
  color: #fff; font-size: 12.5px; font-weight: 600; text-decoration: none;
  &:hover { background: #6d28d9; }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  padding: 12px 16px;

  .thumb {
    width: 44px; height: 44px; border-radius: 8px; flex-shrink: 0;
    background: #0d0d1b; display: flex; align-items: center; justify-content: center;
    overflow: hidden;
    img { width: 100%; height: 100%; object-fit: cover; }
    svg { color: #4b5563; font-size: 18px; }
  }

  .info { flex: 1; min-width: 0; }
  .name { font-size: 13.5px; font-weight: 700; color: #e2e8f0; }
  .meta { font-size: 11.5px; color: #6b7280; margin-top: 2px; }

  .status {
    font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 999px; flex-shrink: 0;
  }

  .cancel-btn {
    display: flex; align-items: center; gap: 5px; flex-shrink: 0;
    background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3);
    color: #f87171; border-radius: 8px; padding: 6px 12px; font-size: 11.5px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    &:hover { background: rgba(239,68,68,0.2); }
  }

  .confirm-actions {
    display: flex; align-items: center; gap: 6px; flex-shrink: 0;
    span { font-size: 11.5px; color: #9ca3af; }
    button {
      border: none; border-radius: 7px; padding: 5px 10px; font-size: 11px; font-weight: 700;
      cursor: pointer; font-family: inherit;
    }
    .yes-btn { background: #ef4444; color: #fff; }
    .no-btn { background: rgba(255,255,255,0.08); color: #9ca3af; }
  }
`;

const STATUS_STYLE = {
  approved: { bg: 'rgba(34,197,94,0.15)', color: '#4ade80' },
  pending: { bg: 'rgba(251,191,36,0.15)', color: '#fbbf24' },
  rejected: { bg: 'rgba(239,68,68,0.15)', color: '#f87171' },
  sold: { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8' },
  cancelled: { bg: 'rgba(239,68,68,0.15)', color: '#f87171' },
};

const MyShopItemsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const { myItems, myItemsLoading } = useSelector(state => state.shopItems);
  const [confirmingId, setConfirmingId] = useState(null);

  useEffect(() => {
    if (auth.isAuth) dispatch(getMyShopItems());
  }, [dispatch, auth.isAuth]);

  if (!auth.isAuth) return <Navigate to="/login" />;

  const statusLabel = (status) => t(`website.my_shop_items.status_${status}`, status);

  const displayStatus = (item) => item.status === 'approved' ? item.item_status : item.status;

  const handleCancel = (id) => {
    dispatch(cancelShopItem({ _id: id }));
    setConfirmingId(null);
  };

  return (
    <WebsiteLayout>
      <Page>
        <Header>
          <div>
            <div className="title"><FiShoppingCart /> {t('website.my_shop_items.title')}</div>
            <div className="sub">{t('website.my_shop_items.sub')}</div>
          </div>
          <AddLink to="/add-shop-item"><FiPlus /> {t('website.shop.post_ad_btn')}</AddLink>
        </Header>

        {myItemsLoading ? null : myItems.length === 0 ? (
          <NoData />
        ) : (
          <List>
            {myItems.map(item => {
              const status = displayStatus(item);
              const style = STATUS_STYLE[status] || STATUS_STYLE.pending;
              const canCancel = item.status === 'approved' && item.item_status === 'available';
              return (
                <Row key={item._id}>
                  <div className="thumb">
                    {item.images?.[0] ? (
                      <img src={`${backendUrl}/shopItems/${item.images[0]}`} alt={item.name} />
                    ) : <FiImage />}
                  </div>
                  <div className="info">
                    <div className="name">{item.name}</div>
                    <div className="meta">{item.type} · {item.category} · {item.price}</div>
                  </div>
                  <span className="status" style={{ background: style.bg, color: style.color }}>
                    {statusLabel(status)}
                  </span>
                  {canCancel && (
                    confirmingId === item._id ? (
                      <div className="confirm-actions">
                        <span>{t('website.my_shop_items.cancel_confirm')}</span>
                        <button className="yes-btn" onClick={() => handleCancel(item._id)}>
                          {t('website.my_shop_items.cancel_yes')}
                        </button>
                        <button className="no-btn" onClick={() => setConfirmingId(null)}>
                          {t('website.my_shop_items.cancel_no')}
                        </button>
                      </div>
                    ) : (
                      <button className="cancel-btn" onClick={() => setConfirmingId(item._id)}>
                        <FiXCircle /> {t('website.my_shop_items.cancel_btn')}
                      </button>
                    )
                  )}
                </Row>
              );
            })}
          </List>
        )}
      </Page>
    </WebsiteLayout>
  );
};

export default MyShopItemsPage;
